import { ElementPositions } from '@/player-config/src/types/RemotionPlayerConfigTypes/CompositionConfig';

export const constrainElementPosition = (positions: ElementPositions, parentDimensions: DOMRect | null) => {
  if (!parentDimensions) {
    return positions;
  }

  const { top, left, width, height } = positions;

  // Convert percentage values to numbers
  const topValue = parseFloat(top?.toString() || '0');
  const leftValue = parseFloat(left?.toString() || '0');
  const widthValue = parseFloat(width?.toString() || '0');
  const heightValue = parseFloat(height?.toString() || '0');

  // Minimum visible portion (10% of the image should always be visible)
  const minVisiblePortion = 0.1;
  const minVisibleWidth = widthValue * minVisiblePortion;
  const minVisibleHeight = heightValue * minVisiblePortion;

  // Calculate boundaries ensuring minimum visible portion
  // For oversized elements, allow movement but ensure they don't go completely off-screen
  const maxLeft = 100 - minVisibleWidth;
  const maxTop = 100 - minVisibleHeight;
  const minLeft = -widthValue + minVisibleWidth;
  const minTop = -heightValue + minVisibleHeight;

  // Apply constraints with Math.max/Math.min for cleaner logic
  const constrainedLeft = Math.max(minLeft, Math.min(maxLeft, leftValue));
  const constrainedTop = Math.max(minTop, Math.min(maxTop, topValue));

  // Final safety check to ensure at least 1% is always visible
  const finalLeft = Math.max(-widthValue + 1, Math.min(99, constrainedLeft));
  const finalTop = Math.max(-heightValue + 1, Math.min(99, constrainedTop));

  return {
    top: `${finalTop}%`,
    left: `${finalLeft}%`,
    width: `${widthValue}%`,
    height: `${heightValue}%`
  };
};

export const calculateMediaBounds = (
  containerElement: HTMLElement | null,
  mediaElement: HTMLImageElement | HTMLVideoElement | HTMLCanvasElement | null
) => {
  if (!containerElement || !mediaElement) {
    return null;
  }

  const containerRect = containerElement.getBoundingClientRect();
  const parentRect = containerElement.parentElement?.getBoundingClientRect();

  if (!containerRect.width || !containerRect.height || !parentRect) {
    return null;
  }

  const naturalWidth =
    'naturalWidth' in mediaElement
      ? mediaElement.naturalWidth
      : 'videoWidth' in mediaElement
        ? mediaElement.videoWidth || 0
        : mediaElement.width;
  const naturalHeight =
    'naturalHeight' in mediaElement
      ? mediaElement.naturalHeight
      : 'videoHeight' in mediaElement
        ? mediaElement.videoHeight || 0
        : mediaElement.height;

  if (!naturalWidth || !naturalHeight) {
    return null;
  }

  const imageAspectRatio = naturalWidth / naturalHeight;
  const containerAspectRatio = containerRect.width / containerRect.height;

  let actualMediaWidth: number;
  let actualMediaHeight: number;
  let offsetX: number;
  let offsetY: number;

  if (imageAspectRatio > containerAspectRatio) {
    // Media is wider than container, fit to width
    actualMediaWidth = containerRect.width;
    actualMediaHeight = containerRect.width / imageAspectRatio;
    offsetX = 0;
    offsetY = (containerRect.height - actualMediaHeight) / 2;
  } else {
    // Media is taller than container, fit to height
    actualMediaHeight = containerRect.height;
    actualMediaWidth = containerRect.height * imageAspectRatio;
    offsetX = (containerRect.width - actualMediaWidth) / 2;
    offsetY = 0;
  }

  // Calculate positions relative to parent
  const containerLeft = containerRect.left - parentRect.left;
  const containerTop = containerRect.top - parentRect.top;
  const mediaLeft = containerLeft + offsetX;
  const mediaTop = containerTop + offsetY;

  // Convert to percentage
  return {
    left: (mediaLeft / parentRect.width) * 100,
    top: (mediaTop / parentRect.height) * 100,
    width: (actualMediaWidth / parentRect.width) * 100,
    height: (actualMediaHeight / parentRect.height) * 100
  };
};
